import React, { useCallback, useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { compose } from 'redux';
import { connect } from 'react-redux';
import * as Cookies from 'js-cookie';
import IconButton from '@material-ui/core/IconButton';
import { ReactComponent as PencilEditIcon } from 'icons/edit.svg';
import moment from 'moment';
import { makeStyles } from '@material-ui/styles';
import { Row } from 'reactstrap';
import Button from 'components/buttons/Button';
import PlusIcon from 'mdi-react/PlusIcon';
import defaultHitImg from 'images/default_hit.jpg';
import Input from 'components/forms/Input';
import Select from 'components/forms/Select';
import Table from 'components/tables';
import TableHead from 'components/tables/tableHead';
import TableBody from 'components/tables/tableBody';
import TableCell from 'components/tables/tableCell';
import TableRow from 'components/tables/tableRow';
import TableSortLabel from 'components/tables/tableSortLabel';
import Tag from 'components/forms/Tag';
// POST MVP import FileDownloadOutlined from 'icons/fileDownloadOutlined';
// POST MVP import ShareOutlined from 'icons/shareOutlined';
import PaginationSetExternalPager from 'components/pagination/PaginationSetExternalPager';
import { Can, AbilityContext } from 'utils/permissions';
import { formatNumber } from 'utils/report';
import {
  DynamicFilter,
  CampaignsFilter,
  OutletsFilter,
  AccountsFilter,
  NotesFilter,
} from 'components/filters';
import LoadingIcon from 'mdi-react/LoadingIcon';
import SelectDateWithCustomRangePicker from 'components/forms/SelectDateWithCustomRangePicker';
import { getOpportunities, updateOpportunity } from 'redux/actions/opportunityActions';
import { Link, withRouter } from 'react-router-dom';
import { getMentions } from 'redux/actions/coverageActions';
import { DATE_FORMAT_VERBOSE, DATE_FORMAT_NO_TIME_ZONE, PAGE_SIZE } from 'constants/config';
import NoResults from 'components/NoResults';
import ConfirmationModal from 'components/modals/ConfirmationModal';
import { get, curry, isEmpty, debounce, camelCase } from 'lodash';
import {
  OPPORTUNITY_STAGES,
  STAGE_OPTIONS,
  getContactLabel,
  getContactFullName,
  STATUS_BADGE_LABEL,
} from 'constants/opportunity';
import HideFields from 'components/Coverage/HideFields';
import { secondaryText } from 'themes/colors';
import AddOpportunity from './AddOpportunity';
import Summary from './Summary';
import SearchDropDownTab from '../Tabs/SearchDropDownTab';
import AddHit from '../Coverage/AddHit';
import { setFiltersToLocation, getFiltersFromLocation } from '../../utils/misc';

const styles = makeStyles(theme => ({
  filterWrapper: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1.5rem',
  },
  whiteWrapper: props => ({
    backgroundColor: theme.palette.common.white,
    borderRadius: '0.375rem',
    paddingBottom: (props.opportunities && props.opportunities.count > PAGE_SIZE) ? '14px' : '0',
  }),
  infoRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '1.5rem 1rem',
  },
  icon: {
    cursor: 'pointer',
  },
  fileDownloadIcon: {
    marginRight: '1rem',
  },
  plusIcon: {
    marginRight: '0.4rem',
    marginTop: '-0.15rem',
  },
  editButton: {
    visibility: 'hidden',
  },
  editIcon: {
    width: '1rem',
    height: '1rem',
    fill: theme.palette.secondary[900],
  },
  editCell: {},
  rottingBubble: {
    padding: '4px 8px',
    borderRadius: '20px',
    backgroundColor: theme.palette.accent3.main,
    marginRight: '8px',
    color: theme.palette.white,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: '700',
  },
  row: {
    '&:hover $editCell $editButton': {
      visibility: 'visible',
    },
    '&.rotting': {
      backgroundColor: theme.palette.accent3[100],

      '&:hover': {
        backgroundColor: theme.palette.accent3[200],
      },
    },
  },
  cell: {
    color: theme.palette.secondary.text,
  },
  rightAlignCell: {
    color: theme.palette.secondary.text,
    paddingRight: 38,
  },
  coverageCell: {
    color: theme.palette.secondary.text,
    padding: '0rem',
  },
  selectedCell: {
    maxWidth: '25rem',
    color: theme.palette.secondary.text,
    border: `1px solid ${theme.palette.autoReply}`,
    zIndex: '1305 !important',
    position: 'relative',
    padding: '0rem',
  },
  chipRow: {
    height: '100%',
    width: '100%',
    padding: '1.3rem 1rem',
    marginRight: '0rem',
    marginLeft: '0rem',
  },
  tagStyle: {
    marginLeft: '0.7rem',
    marginBottom: '0.2rem',
  },
  tagLabelStyle: {
    '& .MuiChip-label': {
      paddingRight: '0rem',
    },
  },
  label: {
    maxWidth: '10rem',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
  },
  logo: {
    borderRadius: '0.375rem',
    width: '1.5rem',
    height: '1.5rem',
    objectFit: 'contain',
    marginLeft: '-0.125rem',
  },
  crossLogo: {
    borderRadius: '0.375rem',
    width: '1.0rem',
    height: '1.0rem',
    objectFit: 'contain',
    marginLeft: '0.350rem',
  },
  articleLink: {
    textDecoration: 'underline',
    fontSize: '0.875rem',
    fontWeight: 600,
  },
  titleRow: {
    paddingLeft: '1.0rem',
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'nowrap !important',
  },
  title: {
    fontWeight: 600,
    fontSize: '12px',
    color: secondaryText,
    cursor: 'pointer',
    margin: 0,
  },
}));

const FILTER_ACCOUNT = 'account';
const FILTER_CAMPAIGN_NAME = 'campaign_name';
const FILTER_OUTLET = 'outlet';
const FILTER_STAGE = 'stage';
const FILTER_UPDATED_DATE = 'last_updated_date';
const FILTER_CIRCLE_BACK_DATE = 'circle_back_date';
const FILTER_CREATED_DATE = 'created_date';
const FILTER_NOTES = 'notes';
const LAST_WEEK_DATE_OPTION = { label: 'Last 7 days', value: 'weeks' };
const LAST_MONTH_DATE_OPTION = { label: 'Last 30 days', value: 'months' };
const CUSTOM_DATE_OPTION = { label: 'Custom', value: 'custom_date_range' };

const defaultFilters = {
  stage: STAGE_OPTIONS.slice(0, 2),
  sequence__campaign_id: [],
  account_id: [],
  outlet_id: [],
  notes: [],
  startLastUpdatedDate: null,
  endLastUpdatedDate: null,
  startCircleBackDate: null,
  endCircleBackDate: null,
  startCreatedDate: null,
  endCreatedDate: null,
  ordering: null,
  lastUpdatedDateSelector: LAST_WEEK_DATE_OPTION,
  circleBackDateSelector: LAST_WEEK_DATE_OPTION,
  createdDateSelector: LAST_WEEK_DATE_OPTION,
};
const emptyFilters = {
  stage: [],
  sequence__campaign_id: [],
  account_id: [],
  outlet_id: [],
  notes: [],
  startLastUpdatedDate: null,
  endLastUpdatedDate: null,
  startCircleBackDate: null,
  endCircleBackDate: null,
  startCreatedDate: null,
  endCreatedDate: null,
  ordering: null,
  lastUpdatedDateSelector: LAST_WEEK_DATE_OPTION,
  circleBackDateSelector: LAST_WEEK_DATE_OPTION,
  createdDateSelector: LAST_WEEK_DATE_OPTION,
};

const Opportunities = ({
  opportunities, getOpportunitiesAction, getMentionsAction, coverages, editOpportunityAction, config, history, location,
}) => {
  const isFilterParamsInQuery = () => get(location, 'search', false);
  const getFiltersFromQweryParams = () => {
    let qweryParams = {};
    if (!isFilterParamsInQuery()) return qweryParams;
    const filtersDataURL = getFiltersFromLocation(location);
    const paramsDataType = [
      'startLastUpdatedDate',
      'endLastUpdatedDate',
      'startCircleBackDate',
      'endCircleBackDate',
      'startCreatedDate',
      'endCreatedDate',
    ];
    const restFilters = { ...filtersDataURL };
    paramsDataType.forEach((paramName) => {
      if (filtersDataURL[paramName]) restFilters[paramName] = moment(new Date(filtersDataURL[paramName]));
    });
    qweryParams = { ...restFilters };
    return qweryParams;
  };
  const classes = styles({ opportunities });
  const initFilters = isFilterParamsInQuery() ? { ...emptyFilters, ...getFiltersFromQweryParams() } : defaultFilters;
  const [filterParams, setFilterParams] = useState(initFilters);
  const [openModal, setOpenModal] = useState(false);
  const [openCoverageModal, setOpenCoverageModal] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [readOnly, setReadOnly] = useState(false);
  const [editCoverage, setEditCoverage] = useState(null);
  const [opportunityToEdit, setOpportunityToEdit] = useState(null);
  const [opportunityCoverageEdit, setOpportunityCoverageEdit] = useState(null);
  const [orderState, setOrderState] = useState('asc');
  const [selectedRow, setSelectedRow] = useState(-1);
  const showFields = JSON.parse(Cookies.get('showFields') || '{}');
  const [areYouSureOpen, setAreYouSureOpen] = useState(false);
  const sorting = orderState === 'asc' ? 'desc' : 'asc';
  const [paginationPager, setPaginationPager] = useState({});

  const ability = useContext(AbilityContext);
  const toggle = (cover) => {
    setEditCoverage(cover);
    setAreYouSureOpen(prevState => !prevState);
  };

  const coverageTile = opp => <>
    <Row
      key={`${opp.id}cell`}
      className={classes.chipRow}
    >
      { opp.mentions.slice(0, 2).map(mention =>
      <>
        <Tag
          className={classes.tagStyle}
          icon={<img
            className={classes.logo}
            src={mention.outlet.logo || defaultHitImg}
            alt="logo"
            width={36}
            height={36}
          />}
          color="gray"
          label={<p className={classes.label}>{mention.article_title}</p>}
          onDelete={selectedRow === opp.id &&
            anchorEl ? () => toggle(mention) : null}
        />
      </>)}
      {opp.mentions.length > 2 && !anchorEl && <>
        <Tag
          className={classes.tagStyle}
          color="gray"
          label={`+${opp.mentions.length - 2}`}
        />
          </>}
      { selectedRow === opp.id &&
        anchorEl && opp.mentions.slice(2).map(mention =>
        <>
          <Tag
            className={classes.tagStyle}
            icon={<img
              className={classes.logo}
              src={mention.outlet.logo || defaultHitImg}
              alt="logo"
              width={36}
              height={36}
            />}
            color="gray"
            label={<p className={classes.label}>{mention.article_title}</p>}
            onDelete={() => toggle(mention)}
          />
        </>)}
    </Row>
  </>;
  const [fieldsToShow, setFieldsToShow] = useState({
    created: {
      show: isEmpty(showFields) || showFields.date,
      label: 'Created',
      field: 'created_date',
      valuePath: 'created_date',
      content: value => moment(value).local().format(DATE_FORMAT_VERBOSE),
    },
    outlet: {
      show: isEmpty(showFields) || showFields.outlet,
      label: 'Outlet',
      valuePath: 'outlet.name',
      field: 'outlet__name',
      redirect: (value) => {
        const url = get(value, 'outlet.website', '#') || '#';
        if (!url.startsWith('http')) {
          return `https://${url}`;
        }
        return url;
      },
      permission: () => true,
      content: value => value,
    },
    contact: {
      show: isEmpty(showFields) || showFields.contact,
      label: 'Contact',
      field: 'contact',
      valuePath: 'contact',
      content: contact => getContactFullName(contact),
      redirect: () => '/indox',
      permission: () => ability.can('see', 'Inbox'),
    },
    stage: {
      show: isEmpty(showFields) || showFields.stage,
      label: 'Stage',
      field: 'stage',
      valuePath: 'stage',
      content: value => STATUS_BADGE_LABEL[value],
    },
    reach: {
      show: isEmpty(showFields) || showFields.reach,
      label: 'Reach',
      field: 'outlet__potential_reach',
      align: 'right',
      valuePath: 'outlet.potential_reach',
      content: value => (!Number.isNaN(parseInt(value, 10)) ? formatNumber(parseInt(value, 10)) : ''),
    },
    uvpm: {
      show: isEmpty(showFields) || showFields.uvpm,
      label: 'UVPM',
      field: 'outlet__unique_monthly_visitors',
      align: 'right',
      valuePath: 'outlet.unique_monthly_visitors',
      content: value => (!Number.isNaN(parseInt(value, 10)) ? formatNumber(parseInt(value, 10)) : ''),
    },
    domainAuthority: {
      show: isEmpty(showFields) || showFields.domainAuthority,
      label: 'Domain Authority',
      align: 'right',
      valuePath: 'outlet.domain_authority',
      content: value => (!Number.isNaN(parseInt(value, 10)) ? formatNumber(parseInt(value, 10)) : ''),
    },
    campaign: {
      show: isEmpty(showFields) || showFields.campaign,
      label: 'Campaign',
      field: 'sequence__campaign__name',
      valuePath: 'sequence.campaign.name',
      redirect: (value) => {
        const url = get(value, 'sequence.campaign.id');
        return `/campaigns/${url}`;
      },
      permission: () => ability.can('see', 'Campaign'),
      content: value => value,
    },
    sequence: {
      show: showFields.sequence,
      label: 'Sequence',
      field: 'sequence__name',
      valuePath: 'sequence.name',
      redirect: (value) => {
        const url = get(value, 'sequence.id');
        return `/sequences/${url}`;
      },
      permission: () => ability.can('see', 'Sequence'),
      content: value => value,
    },
    coverage: {
      show: isEmpty(showFields) || showFields.coverage,
      label: 'Coverage',
      valuePath: 'coverage',
      content: value => value,
    },
    notes: {
      show: showFields.notes,
      label: 'Notes',
      field: 'notes',
      valuePath: 'notes',
      content: value => value,
    },
    updated: {
      show: showFields.updated,
      label: 'Updated',
      field: 'modified_date',
      valuePath: 'modified_date',
      content: value => moment(value).local().format(DATE_FORMAT_VERBOSE),
    },
    ...(config.opportunityRotting ? {
      circleBack: {
        show: showFields.circleBack,
        label: 'Circle Back',
        field: 'circle_back_date',
        valuePath: 'circle_back_date',
        content: value => (value ? moment(value).local().format(DATE_FORMAT_VERBOSE) : ''),
      },
    }
      : {}
    ),
    type: {
      show: showFields.type,
      label: 'Type',
      field: 'outlet__outlet_type',
      valuePath: 'outlet.outlet_type',
      content: value => value,
    },
    country: {
      show: showFields.country,
      label: 'Country',
      field: 'outlet__country',
      valuePath: 'outlet.country',
      content: value => value || '',
    },
    account: {
      show: showFields.account,
      label: 'Account',
      field: 'account__name',
      valuePath: 'account.name',
      content: value => value,
    },
  });
  const prepareFilterParams = (payload) => {
    const params = { ...payload };

    delete params.createdDateSelector;
    delete params.lastUpdatedDateSelector;
    delete params.circleBackDateSelector;

    if (sorting === 'desc' && payload.ordering) {
      params.ordering = `-${payload.ordering}`;
    }
    params.outlet_id = payload.outlet_id.map(l => (l.value)).join(',');
    params.stage = payload.stage.map(l => (l.value)).join(',');
    params.sequence__campaign_id = payload.sequence__campaign_id.map(l => (l.value)).join(',');
    params.account_id = payload.account_id.map(l => (l.value)).join(',');
    params.last_updated_date_after = payload.startLastUpdatedDate
      ? moment(params.startLastUpdatedDate).startOf('day').format(DATE_FORMAT_NO_TIME_ZONE)
      : null;
    params.last_updated_date_before = payload.endLastUpdatedDate
      ? moment(params.endLastUpdatedDate).endOf('day').format(DATE_FORMAT_NO_TIME_ZONE)
      : null;
    delete params.startLastUpdatedDate;
    delete params.endLastUpdatedDate;

    // CIRCLE BACK
    params.circle_back_date_after = payload.startCircleBackDate
      ? moment(params.startCircleBackDate).startOf('day').format(DATE_FORMAT_NO_TIME_ZONE)
      : null;
    params.circle_back_date_before = payload.endCircleBackDate
      ? moment(params.endCircleBackDate).endOf('day').format(DATE_FORMAT_NO_TIME_ZONE)
      : null;
    delete params.startCircleBackDate;
    delete params.endCircleBackDate;

    // CREATED
    params.created_date_after = payload.startCreatedDate
      ? moment(params.startCreatedDate).startOf('day').format(DATE_FORMAT_NO_TIME_ZONE)
      : null;
    params.created_date_before = payload.endCreatedDate
      ? moment(params.endCreatedDate).endOf('day').format(DATE_FORMAT_NO_TIME_ZONE)
      : null;
    delete params.startCreatedDate;
    delete params.endCreatedDate;
    return params;
  };

  const toggleModal = (refreshList) => {
    if (openModal) {
      setOpportunityToEdit(null);
    }
    setOpenModal(!openModal);
    if (refreshList && !readOnly) {
      getOpportunitiesAction(prepareFilterParams({ ...filterParams, page: paginationPager?.currentPage }));
    }
  };

  const toggleCoverageModal = () => {
    setAnchorEl(null);
    setOpenCoverageModal(prevState => !prevState);
  };

  useEffect(() => {
    const params = prepareFilterParams(filterParams);
    const { date_before: dateBefore, date_after: dateAfter } = params;
    if ((dateBefore && dateAfter) || (!dateBefore && !dateAfter)) {
      setFiltersToLocation(location, history, filterParams);
      getOpportunitiesAction(params);
    }
  }, [filterParams]);

  const isURLStoringValue = (name) => {
    if (!isFilterParamsInQuery()) return false;
    const filtersData = getFiltersFromLocation(location);
    switch (name) {
      case FILTER_ACCOUNT:
        return !isEmpty(filtersData?.account_id);
      case FILTER_CAMPAIGN_NAME:
        return !isEmpty(filtersData?.sequence__campaign_id);
      case FILTER_OUTLET:
        return !isEmpty(filtersData?.outlet_id);
      case FILTER_STAGE:
        return !isEmpty(filtersData?.stage);
      case FILTER_NOTES:
        return !isEmpty(filtersData?.notes);
      case FILTER_UPDATED_DATE:
        return Boolean(filtersData?.startLastUpdatedDate || filtersData?.endLastUpdatedDate);
      case FILTER_CIRCLE_BACK_DATE:
        return Boolean(filtersData?.startCircleBackDate || filtersData?.endCircleBackDate);
      case FILTER_CREATED_DATE:
        return Boolean(filtersData?.startCreatedDate || filtersData?.endCreatedDate);
      default: return false;
    }
  };

  const handleFilterChange = (name, value) => {
    setFilterParams(prevFilterParams => ({ ...prevFilterParams, [name]: value }));
  };

  const handleChangeDate = dateVariable => (startDate, endDate) => {
    const newState = {
      ...filterParams,
      [`start${dateVariable}Date`]: startDate,
      [`end${dateVariable}Date`]: endDate,
    };
    setFilterParams(newState);
  };

  const handleViewDetail = (opportunity) => {
    setOpportunityToEdit(opportunity);
    setReadOnly(true);
    toggleModal();
  };

  const resetFilter = (filter) => {
    const newParams = {};
    switch (filter) {
      case FILTER_ACCOUNT:
        newParams.account_id = [];
        break;
      case FILTER_CAMPAIGN_NAME:
        newParams.sequence__campaign_id = [];
        break;
      case FILTER_OUTLET:
        newParams.outlet_id = [];
        break;
      case FILTER_UPDATED_DATE:
        newParams.startLastUpdatedDate = null;
        newParams.endLastUpdatedDate = null;
        newParams.lastUpdatedDateSelector = LAST_WEEK_DATE_OPTION;
        break;
      case FILTER_CIRCLE_BACK_DATE:
        newParams.startCircleBackDate = null;
        newParams.endCircleBackDate = null;
        newParams.circleBackDateSelector = LAST_WEEK_DATE_OPTION;
        break;
      case FILTER_CREATED_DATE:
        newParams.startCreatedDate = null;
        newParams.endCreatedDate = null;
        newParams.createdDateSelector = LAST_WEEK_DATE_OPTION;
        break;
      case FILTER_STAGE:
        newParams.stage = [];
        break;
      case FILTER_NOTES:
        newParams.notes = [];
        break;
      default:
        break;
    }
    return newParams;
  };

  const onResetSelectedFilter = (filter) => {
    setFilterParams({ ...filterParams, ...resetFilter(filter) });
  };

  const onResetFilters = () => {
    const newState = {
      ...defaultFilters,
      stage: [],
    };
    setFilterParams(newState);
  };

  const handleEditClick = (opportunity) => {
    setOpportunityToEdit(opportunity);
    setReadOnly(false);
    toggleModal();
  };

  const handleOrdering = (ordering) => {
    setOrderState(sorting);
    setFilterParams({ ...filterParams, ordering });
  };

  const handleDateChangeFilter = (dateSelector, dateVariable, state) => {
    let { startDate, endDate } = filterParams;
    switch (dateSelector) {
      case LAST_WEEK_DATE_OPTION:
        startDate = moment().subtract(7, 'd').local();
        endDate = moment().local();
        break;
      case LAST_MONTH_DATE_OPTION:
        startDate = moment().subtract(1, 'months').local();
        endDate = moment().local();
        break;
      case CUSTOM_DATE_OPTION:
        setFilterParams({
          ...filterParams,
          ...state,
          [`start${dateVariable}Date`]: null,
          [`end${dateVariable}Date`]: null,
          [camelCase(`${dateVariable}DateSelector`)]: dateSelector,
        });
        return;
      default:
        break;
    }

    setFilterParams({
      ...filterParams,
      ...state,
      [camelCase(`${dateVariable}DateSelector`)]: dateSelector,
      [`start${dateVariable}Date`]: startDate,
      [`end${dateVariable}Date`]: endDate,
    });

    setFilterParams((prevState) => {
      if (startDate && endDate) {
        getOpportunitiesAction(prepareFilterParams({ ...prevState }));
      }
      return prevState;
    });
  };

  const onFilterMount = ((selectedFilter, filterToReset) => {
    const newParams = resetFilter(filterToReset);

    switch (selectedFilter) {
      case FILTER_UPDATED_DATE:
        handleDateChangeFilter(LAST_WEEK_DATE_OPTION, 'LastUpdated', newParams);
        break;
      case FILTER_CIRCLE_BACK_DATE:
        handleDateChangeFilter(LAST_WEEK_DATE_OPTION, 'CircleBack', newParams);
        break;
      case FILTER_CREATED_DATE:
        handleDateChangeFilter(LAST_WEEK_DATE_OPTION, 'Created', newParams);
        break;
      default:
        break;
    }
  });

  const onDeleteFilter = (selectedFilter) => {
    const newParams = {};
    switch (selectedFilter) {
      case FILTER_ACCOUNT:
        newParams.account_id = [];
        break;
      case FILTER_CAMPAIGN_NAME:
        newParams.sequence__campaign_id = [];
        break;
      case FILTER_OUTLET:
        newParams.outlet_id = [];
        break;
      case FILTER_UPDATED_DATE:
        newParams.startLastUpdatedDate = null;
        newParams.endLastUpdatedDate = null;
        newParams.lastUpdatedDateSelector = LAST_WEEK_DATE_OPTION;
        break;
      case FILTER_CIRCLE_BACK_DATE:
        newParams.startCircleBackDate = null;
        newParams.endCircleBackDate = null;
        newParams.circleBackDateSelector = LAST_WEEK_DATE_OPTION;
        break;
      case FILTER_CREATED_DATE:
        newParams.startCreatedDate = null;
        newParams.endCreatedDate = null;
        newParams.createdDateSelector = LAST_WEEK_DATE_OPTION;
        break;
      case FILTER_STAGE:
        newParams.stage = [];
        break;
      case FILTER_NOTES:
        newParams.notes = [];
        break;
      default:
        break;
    }

    setFilterParams({ ...filterParams, ...newParams });
  };
  const handleSelect = (e, opportunity) => {
    setAnchorEl(e.currentTarget);
    setSelectedRow(opportunity.id);
    setOpportunityCoverageEdit(opportunity);
    getMentionsAction({ account__id: opportunity.account.id, has_opportunity: false });
  };

  const curriedHandleFilterChange = curry(handleFilterChange);
  const debouncedHandleFilterChange = useCallback(debounce(handleFilterChange, 600), []);

  const handleSubmit = async (event, id) => {
    const [mention] = coverages.results.filter(coverage => coverage.id === id);
    setAnchorEl(null);
    event.preventDefault();
    await editOpportunityAction({
      id: opportunityCoverageEdit.id,
      title: opportunityCoverageEdit.title,
      stage: opportunityCoverageEdit.stage,
      account_id: opportunityCoverageEdit.account.id,
      contact_id: opportunityCoverageEdit.contact.id,
      outlet_id: isEmpty(opportunityCoverageEdit.outlet) ? null : opportunityCoverageEdit.outlet.id,
      sequence_id: isEmpty(opportunityCoverageEdit.sequence) ? null : opportunityCoverageEdit.sequence.id,
      mention_ids: [...opportunityCoverageEdit.mentions.map(obj => obj.id), mention.id],
    });
  };

  const removeCoverage = async (event, id) => {
    event.stopPropagation();
    setAnchorEl(null);
    await editOpportunityAction({
      id: opportunityCoverageEdit.id,
      title: opportunityCoverageEdit.title,
      stage: opportunityCoverageEdit.stage,
      account_id: opportunityCoverageEdit.account.id,
      contact_id: opportunityCoverageEdit.contact.id,
      outlet_id: isEmpty(opportunityCoverageEdit.outlet) ? null : opportunityCoverageEdit.outlet.id,
      sequence_id: isEmpty(opportunityCoverageEdit.sequence) ? null : opportunityCoverageEdit.sequence.id,
      mention_ids: opportunityCoverageEdit.mentions
        .filter(obj => obj.id !== id)
        .map(obj => obj.id),
    });
  };

  const handleLoadMentions = async (search) => {
    await getMentionsAction({ ...search, has_opportunity: false });
  };
  const debouncedHandleCoverageSearch = useCallback(debounce(handleLoadMentions, 400), []);

  const toggleAreYouSure = (e, answer) => {
    if (answer) {
      setAreYouSureOpen(false);
      removeCoverage(e, editCoverage.id);
    } else {
      setAreYouSureOpen(answer);
    }
  };

  const dateOptions = [
    LAST_WEEK_DATE_OPTION,
    LAST_MONTH_DATE_OPTION,
    CUSTOM_DATE_OPTION,
  ];

  return (
    <div>
      <div className={classes.filterWrapper}>
        <div className="mr-2">
          <DynamicFilter
            onResetSelectedFilter={onResetSelectedFilter}
            onResetFilters={onResetFilters}
            menuDescription="Show all opportunities where:"
            options={[
              ...(
                ability.can('see', 'Account') ? [
                  {
                    key: FILTER_ACCOUNT,
                    menuLabel: 'Accounts',
                    onDeleteFilter,
                  isDefault: isURLStoringValue(FILTER_ACCOUNT),
                  component: <AccountsFilter
                    value={filterParams.account_id}
                    onChange={curriedHandleFilterChange('account_id')}
                  />,
                  },
                ] : []),
                {
                  key: FILTER_CAMPAIGN_NAME,
                  menuLabel: 'Campaign Name',
                  onDeleteFilter,
                  isDefault: isURLStoringValue(FILTER_CAMPAIGN_NAME),
                  component: <CampaignsFilter
                    value={filterParams.sequence__campaign_id}
                    onChange={curriedHandleFilterChange('sequence__campaign_id')}
                    accountIds={filterParams.account_id}
                    includeUnknownOption
                  />,
                },
                {
                  key: FILTER_OUTLET,
                  menuLabel: 'Outlets',
                  onDeleteFilter,
                  isDefault: isURLStoringValue(FILTER_OUTLET),
                  component: <OutletsFilter
                    value={filterParams.outlet_id}
                    onChange={curriedHandleFilterChange('outlet_id')}
                  />,
                },
                {
                  key: FILTER_STAGE,
                  menuLabel: 'Stage',
                  onDeleteFilter,
                  isDefault: !isFilterParamsInQuery() || isURLStoringValue(FILTER_STAGE),
                  component: <Select
                    isMulti
                    value={filterParams.stage}
                    onChange={curriedHandleFilterChange('stage')}
                    options={STAGE_OPTIONS}
                  />,
                },
                {
                  key: FILTER_NOTES,
                  menuLabel: 'Notes',
                  onDeleteFilter,
                  isDefault: isURLStoringValue(FILTER_NOTES),
                  component: <NotesFilter
                    value={filterParams.notes}
                    onChange={curriedHandleFilterChange('notes')}
                  />,
                },
                {
                  key: FILTER_UPDATED_DATE,
                  menuLabel: 'Last updated',
                  onDeleteFilter,
                  onFilterMount,
                  isDefault: isURLStoringValue(FILTER_UPDATED_DATE),
                  component: (
                    <SelectDateWithCustomRangePicker
                      dateOptions={dateOptions}
                      dateSelector={filterParams.lastUpdatedDateSelector}
                      onChangeDate={handleChangeDate('LastUpdated')}
                      onDateChangeFilter={selector => handleDateChangeFilter(selector, 'LastUpdated', filterParams)}
                      startDate={filterParams.startLastUpdatedDate}
                      endDate={filterParams.endLastUpdatedDate}
                    />),
                },
                {
                  key: FILTER_CIRCLE_BACK_DATE,
                  menuLabel: 'Circle Back Date',
                  onDeleteFilter,
                  onFilterMount,
                  isDefault: isURLStoringValue(FILTER_CIRCLE_BACK_DATE),
                  component: (
                    <SelectDateWithCustomRangePicker
                      dateOptions={dateOptions}
                      dateSelector={filterParams.circleBackDateSelector}
                      onChangeDate={handleChangeDate('CircleBack')}
                      onDateChangeFilter={selector => handleDateChangeFilter(selector, 'CircleBack', filterParams)}
                      startDate={filterParams.startCircleBackDate}
                      endDate={filterParams.endCircleBackDate}
                    />),
                },
                {
                  key: FILTER_CREATED_DATE,
                  menuLabel: 'Created',
                  onDeleteFilter,
                  onFilterMount,
                    isDefault: isURLStoringValue(FILTER_CREATED_DATE),
                    component: (
                      <SelectDateWithCustomRangePicker
                        dateOptions={dateOptions}
                        dateSelector={filterParams.createdDateSelector}
                        onChangeDate={handleChangeDate('Created')}
                        onDateChangeFilter={selector => handleDateChangeFilter(selector, 'Created', filterParams)}
                        startDate={filterParams.startCreatedDate}
                        endDate={filterParams.endCreatedDate}
                      />),
                },
            ]}
          />
        </div>
        <div className="search-input-form w-100">
          <Input
            inputProps={{ type: 'search' }}
            rightIcon="search"
            onChange={e => debouncedHandleFilterChange('search', e.target.value)}
            placeholder="Search"
          />
        </div>
        <Can I="add" a="Opportunity">
          <div className="btn-container ml-2">
            <Button
              color="primary"
              variant="contained"
              size="large"
              onClick={() => {
                toggleModal();
                setReadOnly(false);
              }}
            >
              <PlusIcon className={classes.plusIcon} />Add opportunity
            </Button>
          </div>
        </Can>
        {openModal && (
          <AddOpportunity
            readOnly={readOnly}
            toggle={toggleModal}
            initialValues={opportunityToEdit && {
              id: opportunityToEdit.id,
              title: opportunityToEdit.title,
              type: isEmpty(opportunityToEdit.outlet) ? null : opportunityToEdit.outlet.outlet_type,
              stage: STAGE_OPTIONS.find(opt => opt.value === opportunityToEdit.stage),
              contact: {
                label: getContactLabel(opportunityToEdit.contact),
                value: opportunityToEdit.contact.id,
              },
              outlet: {
                label: get(opportunityToEdit, 'outlet.name', null),
                value: get(opportunityToEdit, 'outlet.id', null),
              },
              account: {
                label: get(opportunityToEdit, 'account.name', null),
                value: get(opportunityToEdit, 'account.id', null),
              },
              campaign: !isEmpty(get(opportunityToEdit, 'sequence.campaign', null)) && {
                label: get(opportunityToEdit, 'sequence.campaign.name', null),
                value: get(opportunityToEdit, 'sequence.campaign.id', null),
              },
              sequence: {
                label: get(opportunityToEdit, 'sequence.name', null),
                value: get(opportunityToEdit, 'sequence.id', null),
              },
              coverage: opportunityToEdit.mentions.map(mention => ({
                label: mention.article_title,
                value: mention.id,
              })),
              notes: opportunityToEdit.notes,
              updatedDate: opportunityToEdit.modified_date,
              modifiedBy: opportunityToEdit.modified_by,
              circleBackDate: config.opportunityRotting ? opportunityToEdit.circle_back_date : undefined,
            }}
          />
        )}
      </div>
      <div className={classes.whiteWrapper}>
        {!isEmpty(opportunities.results) && (
          <>
            <div className={classes.infoRow}>
              <HideFields
                fieldsToShow={fieldsToShow}
                onFieldsToShowChange={setFieldsToShow}
                cookieKey="oppShowField"
              />
              <Summary
                count={opportunities.count}
                totalReach={opportunities.total_potential_reach}
                averageDaysRotting={opportunities.average_days_rotting}
                numberOfRottingOpportunities={opportunities.number_of_rotting_opportunities}
              />
              <div>
                {/* POST MVP
                <FileDownloadOutlined className={classNames(classes.fileDownloadIcon, classes.icon)} />
                <ShareOutlined className={classes.icon} />
                */}
              </div>
            </div>
            <div className="material-table__wrap">
              {opportunities.loading ? (
                <div className="loader"><LoadingIcon /></div>
              ) : (
                <>
                  <Table className="volt-table">
                    <TableHead>
                      <TableRow>
                        <TableCell
                          onClick={() => handleOrdering('title')}
                          className="sortable truncate-text--200"
                        >
                          <TableSortLabel
                            order={orderState}
                            orderBy={filterParams.ordering}
                            property="title"
                          >
                            Title
                          </TableSortLabel>
                        </TableCell>
                        <Can I="edit" a="Opportunity">
                          <TableCell />
                        </Can>
                        {Object.values(fieldsToShow).map(field => (
                          field.show && (
                            <TableCell
                              key={field.label}
                              alignRight={field.align === 'right'}
                              onClick={field.field ? () => handleOrdering(field.field) : () => {}}
                              className="sortable truncate-text--200"
                            >
                              {field.field ?
                                <TableSortLabel
                                  order={orderState}
                                  orderBy={filterParams.ordering}
                                  property={field.field}
                                >
                                  {field.label}
                                </TableSortLabel> : field.label}
                            </TableCell>
                          )
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {opportunities.results.map((opportunity) => {
                        const {
                          rotting_cadence: rottingCadence,
                          modified_date: modifiedDate,
                          circle_back_date: circleBackDate,
                        } = opportunity;
                        const currentDate = moment().startOf('day');
                        const thresholdDate = moment().subtract(rottingCadence, 'd');
                        const isRotting =
                          !([
                            OPPORTUNITY_STAGES.closed,
                            OPPORTUNITY_STAGES.published,
                            OPPORTUNITY_STAGES.not_now,
                          ].includes(opportunity.stage))
                          && moment(modifiedDate) < thresholdDate
                          && (circleBackDate === null || moment(circleBackDate) < currentDate);
                        const rottingDays = moment(modifiedDate).diff(currentDate, 'd');
                        const getUrlClickContact = () => {
                          const params = new URLSearchParams();
                          const serializedObject = JSON.stringify({ opportunity__id: [{ value: opportunity.id, label: opportunity.title }] });
                          params.append('filters', serializedObject);
                          return `/inbox?${params.toString()}`;
                        };
                        return (
                          <TableRow
                            key={opportunity.id}
                            className={classNames(classes.row, {
                              rotting: ability.can('see', 'Rotting') && isRotting && config.opportunityRotting,
                            })}
                          >
                            <TableCell
                              className={classes.cell}
                            >
                              <Row className={classes.titleRow} onClick={() => handleViewDetail(opportunity)}>
                                {config.opportunityRotting && (
                                  <Can I="see" a="Rotting">
                                    {isRotting && <div className={classes.rottingBubble}>{Math.abs(rottingDays)}d</div>}
                                  </Can>
                                )}
                                <p className={classes.title}>{opportunity.title}</p>
                              </Row>
                            </TableCell>
                            <Can I="edit" a="Opportunity">
                              <TableCell className={classNames(classes.cell, classes.editCell)} alignRight>
                                <IconButton onClick={() => handleEditClick(opportunity)} className={classes.editButton}>
                                  <PencilEditIcon className={classes.editIcon} />
                                </IconButton>
                              </TableCell>
                            </Can>
                            {Object.values(fieldsToShow).map((field) => {
                              if (field.show && field.label !== 'Coverage') {
                                return (
                                  <TableCell
                                    className={field.align === 'right' && field.label !== 'Domain Authority' ?
                                    classes.rightAlignCell : classes.cell}
                                    alignRight={field.align === 'right'}
                                  >
                                    {field.redirect && field.permission() ?
                                      <Link
                                        style={{ textDecoration: 'underline' }}
                                        to={
                                          field.label === 'Contact' ? getUrlClickContact() :
                                          {
                                            pathname: field.redirect(opportunity),
                                            state: {
                                              data: {
                                                label: field.content(get(opportunity, field.valuePath)),
                                                value: get(opportunity, field.redirectPath),
                                                },
                                            },
                                          }
                                        }
                                        target="_blank"
                                      >
                                        {field.content(get(opportunity, field.valuePath))}
                                      </Link> : field.content(get(opportunity, field.valuePath))
                                    }
                                  </TableCell>
                                );
                              } else if (field.show && field.label === 'Coverage') {
                                return (
                                  <TableCell
                                    className={anchorEl && selectedRow === opportunity.id ?
                                      classes.selectedCell : classes.coverageCell}
                                    onClick={ability.can('edit', 'Opportunity') ? e => handleSelect(e, opportunity) :
                                    () => {}}
                                  >
                                    {coverageTile(opportunity)}
                                  </TableCell>
                                );
                              }
                              return null;
                            })}
                          </TableRow>
                        );
                      })}
                      {anchorEl && opportunityCoverageEdit && <SearchDropDownTab
                        options={coverages.results}
                        anchorEl={anchorEl}
                        setAnchorEl={setAnchorEl}
                        seachPlaceholder="Find coverage"
                        toggle={toggleCoverageModal}
                        values={opportunityCoverageEdit.mentions}
                        handleSearch={input => debouncedHandleCoverageSearch({
                          search: input,
                          account__id: opportunityCoverageEdit.account.id,
                        })}
                        isLoading={coverages.loading}
                        actionHandler={handleSubmit}
                      />}
                    </TableBody>
                  </Table>
                  {openCoverageModal && (
                    <AddHit
                      toggle={toggleCoverageModal}
                      initialValues={{
                        outlet: {
                          label: get(opportunityCoverageEdit, 'outlet.name', null),
                          value: get(opportunityCoverageEdit, 'outlet.id', null),
                        },
                        account: {
                          label: get(opportunityCoverageEdit, 'account.name', null),
                          value: get(opportunityCoverageEdit, 'account.id', null),
                        },
                        campaign: !isEmpty(get(opportunityCoverageEdit, 'sequence.campaign', null)) && {
                          label: get(opportunityCoverageEdit, 'sequence.campaign.name', null),
                          value: get(opportunityCoverageEdit, 'sequence.campaign.id', null),
                        },
                        sequence: {
                          label: get(opportunityCoverageEdit, 'sequence.name', null),
                          value: get(opportunityCoverageEdit, 'sequence.id', null),
                        },
                      }}
                    />
                  )}
                  <PaginationSetExternalPager
                    items={opportunities}
                    onChangePage={getOpportunitiesAction}
                    params={prepareFilterParams(filterParams)}
                    setPagerProps={setPaginationPager}
                  />
                </>
              )}
              <ConfirmationModal
                open={areYouSureOpen}
                onBackdropClick={e => toggleAreYouSure(e, false)}
                title="Confirm"
                confirmLabel="Yes, unlink"
                cancelLabel="Cancel"
                onConfirmClick={e => toggleAreYouSure(e, true)}
                onCancelClick={e => toggleAreYouSure(e, false)}
                classes={{ Dialog: { zIndex: 1400 } }}
              >
                Are you sure you want to unlink the coverage “{editCoverage ?
                `${editCoverage.article_title}` : ''}” with this opportunity?
              </ConfirmationModal>
            </div>
          </>
        )}

        <NoResults
          loading={opportunities.loading}
          results={opportunities.results}
        />
      </div>
    </div>
  );
};

Opportunities.propTypes = {
  getOpportunitiesAction: PropTypes.func.isRequired,
  getMentionsAction: PropTypes.func.isRequired,
  editOpportunityAction: PropTypes.func.isRequired,
  config: PropTypes.shape({
    opportunityRotting: PropTypes.bool,
  }).isRequired,
  opportunities: PropTypes.shape({
    loading: PropTypes.bool,
    loaded: PropTypes.bool,
    results: PropTypes.array,
  }).isRequired,
  coverages: PropTypes.shape({
    loading: PropTypes.bool,
    loaded: PropTypes.bool,
    results: PropTypes.array,
  }).isRequired,
  location: PropTypes.shape({
    state: PropTypes.shape({
      data: PropTypes.shape({
        label: PropTypes.string,
        value: PropTypes.number,
      }),
    }),
    path: PropTypes.string.isRequired,
    url: PropTypes.string.isRequired,
    search: PropTypes.string,
  }),
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }),
};
Opportunities.defaultProps = {
  location: {
    state: {},
    pathname: '',
    search: '',
    hash: '',
  },
  history: {
    push: () => {},
  },
};

export default compose(withRouter, connect(
  state => ({
    opportunities: state.opportunities,
    coverages: state.mentions,
    config: state.config,
  }),
  {
    getOpportunitiesAction: getOpportunities,
    getMentionsAction: getMentions,
    editOpportunityAction: updateOpportunity,
  },
))(Opportunities);
